// Creative Analyzer configuration
// Reuses the same Gemini-based agent (with vision capabilities) as the Ad Analyzer
// This agent can analyze both images/videos and performance data

export const CREATIVE_ANALYZER_AGENT_ID = '1769630536650047000-513506'

// Prompt template for analyzing a creative ad set
// Placeholders: {adSetName}, {metrics}, {adsList}, {thumbnailUrls}
export const CREATIVE_ANALYSIS_PROMPT = `You are an expert performance marketing analyst specializing in Meta Ads creative analysis. You analyze ad performance data AND visual creatives to provide actionable insights.

## Context

You are analyzing an **Ad Set** (group of related ads) from Meta Ads Manager. I will provide:
1. Aggregated performance metrics for the ad set
2. Individual ads within this ad set with their metrics
3. Creative thumbnails (if available) for visual analysis

## Ad Set: {adSetName}

### Aggregated Metrics
{metrics}

### Individual Ads
{adsList}

---

## Your Analysis

Please provide your analysis in these sections:

### 1. Performance Summary
- Overall ad set health assessment (Strong / Moderate / Weak)
- Key performance indicators vs industry benchmarks
- Spend efficiency rating

### 2. Winners & Losers
- Which individual ads are performing best and why
- Which ads should be paused or optimized
- Patterns in what works vs what doesn't

### 3. Creative Insights
**IMPORTANT: If images are provided, describe what you see and analyze the visual elements.**
- Common visual elements in top performers
- Copy/messaging patterns that drive results
- Creative fatigue signals (declining CTR, rising CPM)

### 4. Optimization Recommendations
Provide 3-5 specific, actionable recommendations:
- Budget reallocation suggestions
- Creative refresh ideas
- Audience or placement adjustments
- A/B testing opportunities

### 5. Confidence Score
Rate your confidence in this analysis (1-10) based on data quality and volume.

---

Format your response in clean **MARKDOWN** with clear section headers. Be specific and data-driven.`

/**
 * Build the analysis prompt with actual creative data
 */
export function formatCreativeAnalysisPrompt(data: {
  adSetName: string
  metrics: {
    spend: number
    revenue: number
    roas: number
    ctr: number
    cpc: number
    cpm: number
    impressions: number
    clicks: number
    conversions: number
  }
  ads: Array<{
    name: string
    spend: number
    revenue: number
    roas: number
    status: string
    days: number
  }>
}): string {
  const metricsStr = [
    `- **Spend:** $${data.metrics.spend.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    `- **Revenue:** $${data.metrics.revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    `- **ROAS:** ${data.metrics.roas.toFixed(2)}x`,
    `- **CTR:** ${data.metrics.ctr.toFixed(2)}%`,
    `- **CPC:** $${data.metrics.cpc.toFixed(2)}`,
    `- **CPM:** $${data.metrics.cpm.toFixed(2)}`,
    `- **Impressions:** ${data.metrics.impressions.toLocaleString()}`,
    `- **Clicks:** ${data.metrics.clicks.toLocaleString()}`,
    `- **Conversions:** ${data.metrics.conversions.toLocaleString()}`,
  ].join('\n')

  const adsStr = data.ads
    .map((ad, i) => [
      `**${i + 1}. ${ad.name}**`,
      `   - Status: ${ad.status} | Days running: ${ad.days}`,
      `   - Spend: $${ad.spend.toFixed(2)} | Revenue: $${ad.revenue.toFixed(2)} | ROAS: ${ad.roas.toFixed(2)}x`,
    ].join('\n'))
    .join('\n\n')

  return CREATIVE_ANALYSIS_PROMPT
    .replace('{adSetName}', data.adSetName)
    .replace('{metrics}', metricsStr)
    .replace('{adsList}', adsStr || 'No individual ad data available.')
}
